<template>
  <div class="users-page">
    <div class="page-title-bar">
      <h2 class="page-title">Users</h2>
    </div>

    <div class="page-content">
      <div class="search-bar">
        <div class="search-wrapper" :class="{ 'is-open': showDropdown }">
          <div
            class="search-select-wrapper"
            :class="{ 'is-open': showDropdown }"
            @click="toggleDropdown"
          >
            <span class="search-select-label">{{ currentSearchType }}</span>
            <img :src="downarrowIcon" alt="arrow" class="dropdown-arrow" />
          </div>
          <input
            v-model="searchKeyword"
            type="text"
            class="search-input"
            :maxlength="searchKeywordMaxLength"
            placeholder="Search.."
            @input="onSearchKeywordInput"
            @keyup.enter="handleSearch"
          />
          <div v-if="showDropdown" class="dropdown-menu">
            <div
              v-for="option in searchTypeOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ selected: searchType === option.value }"
              @click="selectSearchType(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
        <div class="search-buttons">
          <button class="btn btn-search" @click="handleSearch">Search</button>
          <button class="btn btn-clear" @click="handleClear">Clear</button>
        </div>
      </div>

      <div class="action-bar">
        <span class="count-info" :class="{ 'count-info--pending': pageView === 'loading' }">
          {{ pageView === 'loading' ? 0 : total }} Users
        </span>
      </div>

      <div class="list-body" :aria-busy="pageView === 'loading'">
        <LoadingIndicator v-if="pageView === 'loading'" />
        <div v-else-if="total === 0" class="orders-empty">
          <img :src="imgListBlank" class="orders-empty-img" alt="" />
          <p class="orders-empty-msg">No users found</p>
        </div>
        <div v-else class="users-list">
          <div v-for="user in usersList" :key="user.id" class="user-card">
            <div class="card-header">
              <span class="user-id" :title="user.userNo">
                <span class="user-label">User ID:</span>
                {{ formattedUserNo(user.userNo) }}
              </span>
              <span class="user-mobile">
                <span class="user-label">Registered Mobile:</span>
                {{ user.mobile || 'N/A' }}
              </span>
            </div>
            <div class="card-body">
              <div class="homes-stack">
                <div v-if="user.homes.length === 0" class="home-row home-row--empty">
                  <div class="column-user">
                    <div class="user-details">
                      <div class="user-address user-address--muted">No Home</div>
                    </div>
                  </div>
                  <div class="column-plan" />
                  <div class="column-orders" />
                </div>
                <div
                  v-for="(home, homeIndex) in user.homes"
                  v-else
                  :key="`${user.id}-home-${homeIndex}`"
                  class="home-row"
                  :class="{ 'home-row-divider': homeIndex < user.homes.length - 1 }"
                >
                  <div class="column-user">
                    <div class="user-details">
                      <div class="user-address">{{ home.address }}</div>
                      <div class="user-upcoming">
                        Upcoming:
                        <span
                          class="upcoming-text"
                          :class="{
                            'upcoming-text--link': isUpcomingClickable(home),
                            'upcoming-none': !isUpcomingClickable(home),
                          }"
                          @click="handleUpcomingClick(home)"
                        >
                          {{ home.upcomingPlanLabel }}
                        </span>
                      </div>
                      <div v-if="home.upcomingDisplay" class="user-date">
                        {{ home.upcomingDisplay }}
                      </div>
                    </div>
                  </div>

                  <div class="column-plan">
                    <div class="plan-content">
                      <div
                        class="plan-info"
                        :class="{
                          'plan-unsubscribed': !home.planSubscribed,
                          'plan-link': home.planSubscribed,
                        }"
                        @click="handlePlanClick(user, home)"
                      >
                        {{ home.planText }}
                      </div>
                    </div>
                  </div>

                  <div class="column-orders">
                    <div
                      v-for="(line, index) in home.orderDisplayLines"
                      :key="`${user.id}-${homeIndex}-order-${index}`"
                      class="order-item"
                      :class="{ 'order-item--clickable': isOrderLineClickable(line) }"
                      @click="handleOrderLineClick(user, home, line)"
                    >
                      <template v-if="line.display">
                        <span class="order-text">{{ line.display }}</span>
                      </template>
                      <template v-else-if="line.label && line.count != null">
                        <span class="order-text">{{ line.label }}</span>
                        <span class="order-count">({{ line.count }})</span>
                      </template>
                    </div>
                    <div
                      v-if="home.orderDisplayLines.length === 0 && home.noOrdersText"
                      class="no-orders"
                    >
                      {{ home.noOrdersText }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="account-side">
                <div class="column-status">
                  <div class="status-content">
                    <div
                      class="status-badge"
                      :class="{ inactive: user.status !== USER_LIST_STATUS_ACTIVE }"
                    >
                      {{ user.statusText }}
                    </div>
                    <a
                      href="javascript:void(0)"
                      class="view-details"
                      @click="handleViewDetails(user.id)"
                    >
                      View Details
                    </a>
                  </div>
                </div>

                <div class="column-actions">
                  <div class="actions-box">
                    <button class="action-btn" @click="handleUpdateMobile(user.id)">
                      <img :src="mobileIcon" alt="mobile" class="action-icon-img" />
                      Update Mobile
                    </button>
                    <button
                      v-if="user.status === USER_LIST_STATUS_ACTIVE"
                      class="action-btn"
                      @click="handleDeactivate(user.id)"
                    >
                      <img :src="scheduleIcon" alt="schedule" class="action-icon-img" />
                      Deactivate
                    </button>
                    <button v-else class="action-btn" @click="handleReactivate(user.id)">
                      <img :src="activateIcon" alt="activate" class="action-icon-img" />
                      Reactivate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pagination-wrapper">
        <PageComponent
          :current-page="currentPage"
          :total-pages="Math.max(1, totalPages)"
          @page-change="handlePageChange"
        />
      </div>
    </div>

    <UserDetail
      v-if="showDetailModal && currentUserId"
      :user-id="currentUserId"
      @close="handleDetailClose"
      @updated="handleDetailUpdated"
    />

    <UserMobileStatus
      v-if="showMobileModal"
      :user-id="currentUserId"
      mode="update"
      @close="showMobileModal = false"
      @saved="handleMobileSaved"
    />

    <UserMobileStatus
      v-if="showDeactivateModal"
      :user-id="currentUserId"
      :mode="deactivateMode"
      @close="showDeactivateModal = false"
      @saved="handleDeactivateSaved"
    />

    <div class="toast-area">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, provide, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  DEFAULT_USER_SEARCH_TYPES,
  buildUserSearchTypes,
  getUserSearchTypeDisplayLabel,
  postUserMeta,
  readUsersListPrefill,
  searchUserList,
  USER_LIST_STATUS_ACTIVE,
  type Pagination,
  type UserHomeItem,
  type UserListItem,
  type UserListParams,
  type UserMetaData,
  type UserSearchTypeOption,
} from '@/api/userslist'
import {
  buildUserOrderStatLabelMap,
  normalizeMobileSearchKeyword,
  resolveUserOrderLines,
  resolveUpcomingDisplay,
  pickUpcomingOrderId,
  resolveUpcomingPlanLabel,
  resolveUpcomingService,
  resolveUserDisplayId,
  resolvePlanLabel,
  resolveUserHomes,
  buildUserHomeOrdersListPrefill,
  type UserOrderDisplayLine,
  type OrderStatLabelMap,
} from '@/utils/userListDisplay'
import { sanitizeDigitsOnly } from '@/utils/fieldLimitValidation'
import { getUserDetail, userDetailToListItem } from '@/api/userdetail'
import { writeHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import { writeCareSubscriptionListPrefill } from '@/api/careSubscription'
import { careOrdersDetailRoute, careOrdersListRouteForSub } from '@/utils/careOrdersRoutes'
import { usersDetailRoute, usersRoute } from '@/utils/libraryRoutes'
import mobileIcon from '@/assets/mobile.png'
import scheduleIcon from '@/assets/Schedule.png'
import activateIcon from '@/assets/activate.png'
import downarrowIcon from '@/assets/downarrow.png'
import imgListBlank from '@/assets/list_blank@2x.png'
import ToastComponent from '@/components/ToastComponent.vue'
import { TOAST_DURATION } from '@/constants/toast'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import UserDetail from './UserDetail.vue'
import UserMobileStatus from './UserMobileStatus.vue'

interface UserHomeRow {
  homeId: string
  address: string
  planText: string
  planSubscribed: boolean
  orderDisplayLines: UserOrderDisplayLine[]
  noOrdersText: string
  upcomingService: ReturnType<typeof resolveUpcomingService>
  upcomingPlanLabel: string
  upcomingDisplay: string
}

interface UserRow {
  id: string
  userNo: string
  mobile: string
  status: number
  statusText: string
  homes: UserHomeRow[]
}

type PageView = 'loading' | 'content'

const router = useRouter()
const route = useRoute()
const searchKeyword = ref('')
const searchType = ref(DEFAULT_USER_SEARCH_TYPES[0]?.value ?? '')
const searchTypeOptions = ref<UserSearchTypeOption[]>([...DEFAULT_USER_SEARCH_TYPES])
const MOBILE_SEARCH_MAX_LEN = 10
const DEFAULT_SEARCH_MAX_LEN = 50
const searchKeywordMaxLength = computed(() =>
  isMobileSearchType(searchType.value) ? MOBILE_SEARCH_MAX_LEN : DEFAULT_SEARCH_MAX_LEN,
)
const showDropdown = ref(false)
const total = ref(0)
const totalPagesFromApi = ref(1)
const currentPage = ref(1)
const pageSize = 10
const pageView = ref<PageView>('loading')
const usersList = ref<UserRow[]>([])
const orderStatLabelMap = ref<OrderStatLabelMap>({})
let fetchGeneration = 0
const LOADING_FLASH_MS = 280

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const formattedUserNo = computed(() => (userNo: string) => {
  if (userNo.length > 8) {
    return userNo.slice(0, 8) + '..'
  }
  return userNo
})

const showDetailModal = ref(false)
const showMobileModal = ref(false)
const showDeactivateModal = ref(false)
const currentUserId = ref('')
const deactivateMode = ref<'deactivate' | 'reactivate'>('deactivate')

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function resolveStatusText(status: number | undefined, statusText: string | undefined): string {
  if (status === USER_LIST_STATUS_ACTIVE) return 'Active'
  const text = statusText?.trim()
  return text || 'Inactive'
}

function mapHomeFields(home: UserHomeItem): UserHomeRow {
  const plan = resolvePlanLabel(home)
  return {
    homeId: home.home_id?.trim() || '',
    address: home.address || '',
    planText: plan.text,
    planSubscribed: plan.subscribed,
    orderDisplayLines: resolveUserOrderLines(home, orderStatLabelMap.value),
    noOrdersText: home.no_orders_text ?? '',
    upcomingService: resolveUpcomingService(home.upcoming_service),
    upcomingPlanLabel: resolveUpcomingPlanLabel(home),
    upcomingDisplay: resolveUpcomingDisplay(home),
  }
}

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  toast.type = type
  toast.message = message
  toast.visible = true
  setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

function resolveUpcomingOrderId(home: UserHomeRow): string {
  return pickUpcomingOrderId(home.upcomingService, home.upcomingPlanLabel)
}

function isUpcomingClickable(home: UserHomeRow): boolean {
  return Boolean(resolveUpcomingOrderId(home))
}

function handleUpcomingClick(home: UserHomeRow): void {
  if (!isUpcomingClickable(home)) return
  const orderId = resolveUpcomingOrderId(home)
  if (!orderId) return
  writeHmCareOrdersListPrefill({
    searchField: 'order_id',
    searchQuery: orderId,
    careSubKey: 'hm_orders',
  })
  void router.push(careOrdersDetailRoute(orderId))
}

function handlePlanClick(user: UserRow, home: UserHomeRow): void {
  if (!home.planSubscribed) return
  const homeId = home.homeId.trim()
  if (homeId && homeId.toUpperCase() !== 'N/A') {
    writeCareSubscriptionListPrefill({ searchType: 'home_id', keyword: homeId })
  } else {
    const userId = user.userNo.trim() || user.id.trim()
    if (!userId) {
      showToast('warning', 'User not found.')
      return
    }
    writeCareSubscriptionListPrefill({ searchType: 'user_id', keyword: userId })
  }
  void router.push(careOrdersListRouteForSub('hm_care_subscription'))
}

function isAdOrderStatKey(statKey: string | undefined): boolean {
  const key = (statKey ?? '').trim().toLowerCase()
  return key === 'ad_orders' || key === 'ad'
}

function isOrderLineClickable(line: UserOrderDisplayLine): boolean {
  if (isAdOrderStatKey(line.statKey)) return false
  if (line.count != null && line.count <= 0) return false
  return Boolean(line.label.trim() || line.display.trim())
}

function handleOrderLineClick(
  user: UserRow,
  home: UserHomeRow,
  line: UserOrderDisplayLine,
): void {
  if (!isOrderLineClickable(line)) return
  const prefill = buildUserHomeOrdersListPrefill(
    home.homeId,
    user.userNo.trim() || user.id.trim(),
  )
  if (!prefill) return
  writeHmCareOrdersListPrefill(prefill)
  void router.push(careOrdersListRouteForSub('hm_orders'))
}

provide<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast', showToast)

function handleViewDetails(id: string | number): void {
  void router.push(usersDetailRoute(String(id)))
}

function handleDetailClose(): void {
  if (route.name === 'UserDetail') {
    void router.push(usersRoute())
    return
  }
  showDetailModal.value = false
  currentUserId.value = ''
}

function syncDetailFromRoute(): void {
  if (route.name === 'UserDetail') {
    const userId = String(route.params.userId ?? '').trim()
    if (!userId) {
      void router.replace(usersRoute())
      return
    }
    currentUserId.value = userId
    showDetailModal.value = true
    return
  }
  showDetailModal.value = false
  currentUserId.value = ''
}

watch(
  () => [route.name, route.params.userId] as const,
  () => {
    syncDetailFromRoute()
  },
  { immediate: true },
)

function handleUpdateMobile(id: string | number): void {
  currentUserId.value = String(id)
  showMobileModal.value = true
}

function handleDeactivate(id: string | number): void {
  currentUserId.value = String(id)
  deactivateMode.value = 'deactivate'
  showDeactivateModal.value = true
}

function handleReactivate(id: string | number): void {
  currentUserId.value = String(id)
  deactivateMode.value = 'reactivate'
  showDeactivateModal.value = true
}

function handleDetailUpdated(userId: string): void {
  void updateSingleUser(userId)
}

async function updateSingleUser(userId: string): Promise<void> {
  try {
    const response = await getUserDetail(userId)
    if (response.code !== 200 || !response.data) return

    const updated = mapListItemToUser(userDetailToListItem(response.data))
    const listIndex = usersList.value.findIndex((user) => user.id === userId)
    if (listIndex !== -1) {
      usersList.value[listIndex] = updated
    }
  } catch (err) {
    console.error('Failed to update single user:', err)
    void refreshUsers()
  }
}

function handleMobileSaved(): void {
  showMobileModal.value = false
  void refreshUsers()
}

function handleDeactivateSaved(): void {
  showDeactivateModal.value = false
  void refreshUsers()
}

const currentSearchType = computed(() => {
  const hit = searchTypeOptions.value.find((option) => option.value === searchType.value)
  if (hit) return getUserSearchTypeDisplayLabel(hit)
  const fallback = searchTypeOptions.value[0]
  return fallback ? getUserSearchTypeDisplayLabel(fallback) : 'Mobile No.'
})

function defaultSearchTypeValue(): string {
  return searchTypeOptions.value[0]?.value ?? DEFAULT_USER_SEARCH_TYPES[0]?.value ?? ''
}

function ensureSearchTypeValid(): void {
  const values = new Set(searchTypeOptions.value.map((option) => option.value))
  if (!values.has(searchType.value)) {
    searchType.value = defaultSearchTypeValue()
  }
}

function tryConsumeUsersListPrefill(): void {
  const prefill = readUsersListPrefill()
  if (!prefill) return
  currentPage.value = 1

  if (searchTypeOptions.value.some((o) => o.value === prefill.searchType)) {
    searchType.value = prefill.searchType
  } else if (isMobileSearchType(prefill.searchType)) {
    // 订单详情等写死 mobile_no 时，映射到 meta 里真实的手机号搜索类型
    const mobileOpt = searchTypeOptions.value.find((o) => isMobileSearchType(o.value))
    searchType.value = mobileOpt?.value ?? defaultSearchTypeValue()
  } else {
    ensureSearchTypeValid()
  }

  searchKeyword.value = isMobileSearchType(searchType.value)
    ? sanitizeDigitsOnly(prefill.keyword, MOBILE_SEARCH_MAX_LEN)
    : prefill.keyword
}

async function beginDataLoad(): Promise<void> {
  pageView.value = 'loading'
  try {
    await loadUserMeta()
    tryConsumeUsersListPrefill()
    await wait(LOADING_FLASH_MS)
    await fetchUsersData()
  } finally {
    pageView.value = 'content'
  }
}

function onSearchKeywordInput(event: Event): void {
  if (!isMobileSearchType(searchType.value)) return
  searchKeyword.value = sanitizeDigitsOnly(
    (event.target as HTMLInputElement).value,
    MOBILE_SEARCH_MAX_LEN,
  )
}

function toggleDropdown(): void {
  showDropdown.value = !showDropdown.value
}

function selectSearchType(type: string): void {
  if (searchType.value === type) {
    showDropdown.value = false
    return
  }
  searchType.value = type
  showDropdown.value = false
  if (isMobileSearchType(type)) {
    searchKeyword.value = sanitizeDigitsOnly(searchKeyword.value, MOBILE_SEARCH_MAX_LEN)
  }
  if (searchKeyword.value.trim()) {
    currentPage.value = 1
    void refreshUsers()
  }
}

function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (!target.closest('.search-select-wrapper')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  void beginDataLoad()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const totalPages = computed(() => Math.max(1, totalPagesFromApi.value))

function applyUserList(rawList: UserListItem[]): void {
  usersList.value = rawList.map(mapListItemToUser)
}

function applyListPagination(pagination: Pagination | undefined): void {
  total.value = pagination?.total ?? 0
  totalPagesFromApi.value = Math.max(1, pagination?.total_pages ?? 1)
}

function resetListState(): void {
  usersList.value = []
  total.value = 0
  totalPagesFromApi.value = 1
}

function mapListItemToUser(item: UserListItem): UserRow {
  return {
    id: String(item.id),
    userNo: resolveUserDisplayId(item),
    mobile: item.registered_mobile || item.mobile || item.phone || '',
    status: item.status ?? 0,
    statusText: resolveStatusText(item.status, item.status_text),
    homes: resolveUserHomes(item).map(mapHomeFields),
  }
}

function handleSearch(): void {
  if (searchKeyword.value.length > 50) {
    showToast('error', 'Search keyword cannot exceed 50 characters')
    return
  }
  currentPage.value = 1
  void refreshUsers()
}

function handleClear(): void {
  searchKeyword.value = ''
  searchType.value = defaultSearchTypeValue()
  currentPage.value = 1
  void refreshUsers()
}

function handlePageChange(page: number): void {
  currentPage.value = page
  void refreshUsers()
}

async function loadUserMeta(): Promise<void> {
  try {
    const response = await postUserMeta({ page: 1, page_size: pageSize })
    if (response.code === 200) {
      const meta = response.data as UserMetaData
      orderStatLabelMap.value = buildUserOrderStatLabelMap(meta)
      searchTypeOptions.value = buildUserSearchTypes(meta)
      ensureSearchTypeValid()
      return
    }
  } catch (err) {
    console.error('Failed to load user meta:', err)
  }
  searchTypeOptions.value = [...DEFAULT_USER_SEARCH_TYPES]
  ensureSearchTypeValid()
}

async function refreshUsers(): Promise<void> {
  pageView.value = 'loading'
  usersList.value = []
  try {
    await wait(LOADING_FLASH_MS)
    await fetchUsersData()
  } finally {
    pageView.value = 'content'
  }
}

function isMobileSearchType(type: string): boolean {
  const normalized = type.trim().toLowerCase()
  return normalized === 'mobile_no' || normalized === 'mobile' || normalized.includes('mobile')
}

function buildSearchKeyword(type: string, keyword: string): string {
  if (!isMobileSearchType(type)) return keyword
  return normalizeMobileSearchKeyword(keyword)
}

async function fetchUsersData(): Promise<void> {
  const generation = ++fetchGeneration
  try {
    const keyword = searchKeyword.value.trim()
    const searchParams: Omit<UserListParams, 'page' | 'page_size'> = {}
    if (keyword) {
      searchParams.search_type = searchType.value
      searchParams.keyword = buildSearchKeyword(searchType.value, keyword)
    }

    const params: UserListParams = {
      page: currentPage.value,
      page_size: pageSize,
      ...searchParams,
    }

    const response = await searchUserList(params)
    if (generation !== fetchGeneration) return

    if (response.code === 200) {
      applyUserList(response.data?.list || [])
      applyListPagination(response.data?.pagination)
    } else {
      resetListState()
      showToast('error', response.msg || 'Failed to fetch')
    }
  } catch (err) {
    if (generation !== fetchGeneration) return
    console.error('Network error:', err)
    resetListState()
    showToast('error', 'Network request failed')
  }
}
</script>

<style scoped>
.users-page {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: -10px 0 16px -10px;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  margin-left: -9px;
  margin-right: -12px;
  border: 1px solid #e8e8e8;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 24px;
  background: #fff;
  width: 273px;
  height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.search-wrapper:focus-within,
.search-wrapper.is-open {
  border-color: #666666;
}

.search-select-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.search-select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.search-select-label {
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}
.dropdown-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.search-select-wrapper.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: -1px;
  width: 273px;
  box-sizing: border-box;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  font-weight: 600;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  background: transparent;
  color: #000000;
  padding-left: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.search-input::placeholder {
  color: #cccccc;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.search-buttons {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.btn {
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  height: 32px;
  width: 80px;
}

.btn-search {
  background: #000;
  color: #fff;
}

.btn-search:hover {
  background: #484848;
}

.btn-clear {
  background: #b0b0b0;
  color: #fff;
}

.btn-clear:hover {
  background: #cccccc;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: auto;
  overflow: visible;
}

.user-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  margin-left: -8px;
  margin-right: -10px;
  position: relative;
  z-index: 1;
}

.card-header {
  width: 100%;
  padding: 6px 24px 6px 16px;
  background: #f7f7f7;
  border-bottom: 1px solid #e0e0e0;
  border-top-left-radius: 16px;
  border-top-right-radius: 15px;
  display: flex;
  gap: 17px;
  font-size: 0.8rem;
  align-items: center;
  flex-wrap: wrap;
}

.user-label {
  color: #999;
}

.user-id,
.user-mobile {
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.user-id {
  display: inline-block;
  width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-body {
  display: flex;
  align-items: stretch;
  min-height: 109px;
  height: auto;
  overflow: visible;
}

.homes-stack {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.home-row {
  display: grid;
  grid-template-columns: 528fr 160fr 160fr;
  flex: 1;
  min-height: 109px;
}

.home-row > div {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  box-sizing: border-box;
}

.home-row-divider {
  border-bottom: 1px solid #e0e0e0;
}

.account-side {
  display: flex;
  flex-shrink: 0;
  align-items: stretch;
}

.account-side > div {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.account-side > div:last-child {
  border-right: none;
}

.column-user {
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
}

/* 相对白色卡片最左侧 16px（与 Technicians card-body 列 padding 一致） */
.home-row > .column-user {
  padding: 16px;
  padding-left: 16px;
}

.column-plan,
.column-orders {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.column-status {
  width: 160px;
  min-width: 160px;
}

.column-actions {
  width: 160px;
  min-width: 160px;
  max-width: 160px;
}

/* 与 Technicians 第一列三行间距一致 */
.user-details {
  gap: 8px;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
}

.user-address {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  word-break: break-word;
}

.user-address--muted {
  color: #cccccc;
}

.user-upcoming {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.upcoming-text {
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
}

.upcoming-text--link {
  cursor: pointer;
}

.upcoming-none {
  color: #000000;
  cursor: default;
}

.user-date {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.column-plan {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  width: auto;
}
.plan-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  overflow: visible;
  position: relative;
}
.plan-info {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: center;
  min-width: 160px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-unsubscribed {
  color: #cccccc;
}

.plan-link {
  text-decoration: underline;
  cursor: pointer;
}

.column-orders {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  width: auto;
  text-align: center;
}

.order-item {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  padding: 0;
  text-align: center;
  word-wrap: break-word;
  white-space: normal;
  width: 100%;
}

.order-item--clickable {
  cursor: pointer;
}

.no-orders {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
  padding: 0;
  color: #000000;
  text-align: center;
  width: auto;
}
.order-text {
  color: #000000;
  font-weight: 400;
  line-height: 20px;
  text-decoration: underline;
}

.order-count {
  color: #000000;
  font-weight: 400;
  line-height: 20px;
  margin-left: 4px;
  text-decoration: underline;
}

.column-status {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.status-content {
  min-width: 80px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
}
.status-badge {
  font-size: 14px;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #00aeef;
  text-align: center;
  white-space: nowrap;
}

.status-badge.inactive {
  color: #ff4d4f;
}

.view-details {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-decoration: none;
  background-color: transparent;
  text-align: center;
  white-space: nowrap;
  padding: 0;
  transition: none;
}

.view-details:hover,
.view-details:focus,
.view-details:active {
  color: #000000;
  background-color: transparent;
  text-decoration: none;
}

.column-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.actions-box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  transition: all 0.2s;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.pagination-wrapper {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  margin-right: -10px;
}

.toast-area {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@media (max-width: 1200px) {
  .card-body {
    grid-template-columns: 1fr 1fr;
  }
  .card-body > div {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
}

@media (max-width: 768px) {
  .card-body {
    grid-template-columns: 1fr;
  }
  .search-bar {
    flex-direction: column;
  }
  .search-wrapper {
    width: 100%;
  }
}
</style>
